import React, { useEffect, useMemo, useState, useRef } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  deleteDoc,
  doc,
  getDocs,
  getDoc,
  updateDoc
} from "firebase/firestore";
import { Trash2, Calendar, Target, TrendingUp, Award, ChevronDown, Filter, Star, Edit3, Image as ImageIcon, X, Check } from "lucide-react";

/* ================= CUSTOM DROPDOWN COMPONENT ================= */
const AccountDropdown = ({ accounts, selectedId, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedAcc = accounts.find(a => a.id === selectedId);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div style={styles.dropdownContainer} ref={dropdownRef}>
      <div style={styles.terminalHeader}>
        <div style={{display:'flex', alignItems:'center', gap: 8}}>
          <Target size={14} color="#38bdf8" />
          <span style={styles.terminalTitle}>ACTIVE ACCOUNT</span>
        </div>
      </div>
      <div 
        style={styles.dropdownTrigger} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <div style={styles.selectedContent}>
          {selectedAcc ? (
            <>
              <span style={styles.accName}>{selectedAcc.name}</span>
              <span style={styles.accDetails}>
                {selectedAcc.accountCode || "—"} | {selectedAcc.currency || "₹"}{Number(selectedAcc.size || 0).toLocaleString()} ({selectedAcc.phase || "Live"})
              </span>
            </>
          ) : (
            <span style={{ color: "#475569" }}>Select Account</span>
          )}
        </div>
        <ChevronDown 
          size={18} 
          style={{ 
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', 
            transition: '0.3s',
            color: '#38bdf8' 
          }} 
        />
      </div>

      {isOpen && (
        <div style={styles.dropdownMenu} className="custom-scrollbar">
          {accounts.length === 0 && (
            <div style={{padding: '12px', color: '#64748b', textAlign: 'center', fontSize: '12px'}}>No accounts found</div>
          )}
          {accounts.map(acc => (
            <div 
              key={acc.id} 
              className="dropdown-item"
              style={{
                ...styles.dropdownOption, 
                borderLeft: selectedId === acc.id ? '3px solid #38bdf8' : '3px solid transparent',
                background: selectedId === acc.id ? 'rgba(56, 189, 248, 0.1)' : 'transparent'
              }}
              onClick={() => { 
                onSelect(acc.id); 
                setIsOpen(false); 
              }}
            >
              <div style={styles.optionMain}>{acc.name} <span style={{color: '#64748b', fontSize: '11px', fontWeight: 400}}>({acc.accountCode})</span></div>
              <div style={styles.optionSub}>
                {acc.currency || "₹"}{Number(acc.size || 0).toLocaleString()} 
                <span style={styles.phaseBadge}>{acc.phase}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ================= COLUMN FILTER COMPONENT ================= */
const ColumnFilter = ({ column, trades, activeFilters, onFilterChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const filterRef = useRef(null);

  const uniqueValues = useMemo(() => {
    const vals = trades.map(t => t[column] || "—");
    return [...new Set(vals)].sort();
  }, [trades, column]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = activeFilters[column] && activeFilters[column] !== "All";

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={filterRef}>
      <Filter 
        size={10} 
        style={{ cursor: 'pointer', marginLeft: 4, color: isActive ? '#38bdf8' : '#475569' }} 
        onClick={() => setIsOpen(!isOpen)} 
      />
      {isOpen && (
        <div style={styles.filterMenu} className="custom-scrollbar">
          <div 
            style={{...styles.filterOption, color: !isActive ? '#38bdf8' : '#94a3b8'}}
            onClick={() => { onFilterChange(column, "All"); setIsOpen(false); }}
          >
            ALL
          </div>
          {uniqueValues.map(val => (
            <div 
              key={val} 
              style={{...styles.filterOption, color: activeFilters[column] === val ? '#38bdf8' : '#94a3b8'}}
              onClick={() => { onFilterChange(column, val); setIsOpen(false); }}
            >
              {String(val).toUpperCase()}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function TradeJournal() {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [trades, setTrades] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [previewImage, setPreviewImage] = useState(null);
  const [editingTrade, setEditingTrade] = useState(null);
  const [tempNote, setTempNote] = useState("");
  
  // State for column filters
  const [activeFilters, setActiveFilters] = useState({});

  const applyPreset = (type) => {
    const today = new Date();
    const fmt = d => d.toISOString().split("T")[0];
    if (type === "today") { setFromDate(fmt(today)); setToDate(fmt(today)); }
    if (type === "7d") { const f = new Date(today); f.setDate(today.getDate() - 6); setFromDate(fmt(f)); setToDate(fmt(today)); }
    if (type === "30d") { const f = new Date(today); f.setDate(today.getDate() - 29); setFromDate(fmt(f)); setToDate(fmt(today)); }
    if (type === "mtd") { const f = new Date(today.getFullYear(), today.getMonth(), 1); setFromDate(fmt(f)); setToDate(fmt(today)); }
  };

  useEffect(() => {
    if (!auth.currentUser) return;
    const q = query(collection(db, "users", auth.currentUser.uid, "accounts"), orderBy("name"));
    const unsub = onSnapshot(q, snap => {
      const accs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setAccounts(accs);
      if (!selectedAccountId && accs.length) setSelectedAccountId(accs[0].id);
    });
    return () => unsub();
  }, [selectedAccountId]);

  useEffect(() => {
    if (!auth.currentUser || !selectedAccountId) return;
    const q = query(collection(db, "users", auth.currentUser.uid, "accounts", selectedAccountId, "trades"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setTrades(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [selectedAccountId]);

  const handleFilterChange = (column, value) => {
    setActiveFilters(prev => ({ ...prev, [column]: value }));
  };

  const filteredTrades = useMemo(() => {
    // 1. Filter the data
    const filtered = trades.filter(t => {
      // Date Range Filter
      if (fromDate || toDate) {
        if (!t.date) return false;
        const d = new Date(t.date);
        if (fromDate && d < new Date(fromDate)) return false;
        if (toDate && d > new Date(toDate)) return false;
      }
      
      // Column Filters
      for (let col in activeFilters) {
        if (activeFilters[col] !== "All") {
            const tradeVal = t[col] || "—";
            if (tradeVal !== activeFilters[col]) return false;
        }
      }

      return true;
    });

    // 2. Sort by date (Descending - Newest First)
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    
  }, [trades, fromDate, toDate, activeFilters]);

  const account = useMemo(() => accounts.find(a => a.id === selectedAccountId), [accounts, selectedAccountId]);
  const currency = account?.currency === "$" ? "$" : "₹";

  const deleteTrade = async (tradeId) => {
    if (!window.confirm("Delete this trade?")) return;
    try {
      const userId = auth.currentUser.uid;
      const accountRef = doc(db, "users", userId, "accounts", selectedAccountId);
      const tradeRef = doc(db, "users", userId, "accounts", selectedAccountId, "trades", tradeId);
      await deleteDoc(tradeRef);
      const tradesRef = collection(db, "users", userId, "accounts", selectedAccountId, "trades");
      const snap = await getDocs(tradesRef);
      const recalculatedPnL = snap.docs.reduce((sum, d) => sum + Number(d.data().pnl || 0), 0);
      const accSnap = await getDoc(accountRef);
      if (accSnap.exists()) {
        const acc = accSnap.data();
        const updates = { currentPnL: recalculatedPnL };
        if (acc.ddType === "trailing") {
          const peakEquity = Math.max(acc.size || 0, (acc.size || 0) + recalculatedPnL);
          updates.peakEquity = peakEquity;
          updates.trailingFloor = peakEquity - ((acc.size * acc.maxTotalDD) / 100);
        }
        await updateDoc(accountRef, updates);
      }
    } catch (err) { console.error(err); }
  };

  const saveNotes = async () => {
    if (!editingTrade) return;
    try {
      const tradeRef = doc(db, "users", auth.currentUser.uid, "accounts", selectedAccountId, "trades", editingTrade.id);
      await updateDoc(tradeRef, { postTradeNotes: tempNote });
      setEditingTrade(null);
    } catch (err) { console.error(err); }
  };

  const stats = useMemo(() => {
    const total = filteredTrades.length;
    const wins = filteredTrades.filter(t => t.result?.toLowerCase() === "profit");
    const losses = filteredTrades.filter(t => t.result?.toLowerCase() === "loss");
    const grossProfit = wins.reduce((s, t) => s + Number(t.pnl || 0), 0);
    const grossLoss = Math.abs(losses.reduce((s, t) => s + Number(t.pnl || 0), 0));
    let running = 0, peak = 0, maxDD = 0;
    filteredTrades.forEach(t => {
      running += Number(t.pnl || 0);
      peak = Math.max(peak, running);
      maxDD = Math.min(maxDD, running - peak);
    });
    return {
      total, wins: wins.length, losses: losses.length,
      winRate: total ? ((wins.length / total) * 100).toFixed(1) : "0.0",
      pnl: grossProfit - grossLoss,
      avgWin: wins.length ? grossProfit / wins.length : 0,
      avgLoss: losses.length ? grossLoss / losses.length : 0,
      expectancy: total ? (grossProfit - grossLoss) / total : 0,
      profitFactor: grossLoss ? grossProfit / grossLoss : 0,
      maxDrawdown: Math.abs(maxDD)
    };
  }, [filteredTrades]);

  const getDayName = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short' });
  };

  return (
    <div style={styles.container}>
      <style>{customCSS}</style>

      {/* IMAGE PREVIEW MODAL */}
      {previewImage && (
        <div style={styles.modalOverlay} onClick={() => setPreviewImage(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <button style={styles.closeBtn} onClick={() => setPreviewImage(null)}><X size={20}/></button>
            <img src={previewImage} alt="Trade Proof" style={styles.fullImage} />
          </div>
        </div>
      )}

      {/* IN-TABLE NOTE EDITOR MODAL */}
      {editingTrade && (
        <div style={styles.modalOverlay} onClick={() => setEditingTrade(null)}>
          <div style={styles.noteEditorBox} onClick={e => e.stopPropagation()}>
            <div style={styles.noteEditorHeader}>
              <div style={{display:'flex', alignItems:'center', gap: 8}}>
                <Edit3 size={14} color="#38bdf8" />
                <span style={{fontSize: 10, fontWeight: 800, color: '#94a3b8'}}>EDIT POST-TRADE NOTES</span>
              </div>
              <X size={16} color="#475569" style={{cursor:'pointer'}} onClick={() => setEditingTrade(null)} />
            </div>
            <textarea 
              style={styles.noteTextarea} 
              value={tempNote} 
              onChange={(e) => setTempNote(e.target.value)}
              placeholder="Write your trade analysis here..."
              autoFocus
            />
            <button style={styles.saveNoteBtn} onClick={saveNotes}>
              <Check size={14} /> SAVE ANALYSIS
            </button>
          </div>
        </div>
      )}
      
      <div style={{...styles.header, marginTop: '-5px'}}>
        <div style={styles.headerLeft}>
          <h1 style={styles.mainTitle}>Trade Journal</h1>
          <p style={styles.subtitle}>Track your performance and refine your edge.</p>
          <AccountDropdown accounts={accounts} selectedId={selectedAccountId} onSelect={setSelectedAccountId} />
        </div>

        <div style={styles.dateSelectorTerminal}>
            <div style={styles.terminalHeader}>
                <div style={{display:'flex', alignItems:'center', gap: 8}}>
                    <Filter size={14} color="#38bdf8" />
                    <span style={styles.terminalTitle}>TIME RANGE FILTER</span>
                </div>
                <div style={styles.presetGroup}>
                    {["today", "7d", "30d", "mtd"].map(p => (
                        <button key={p} className="terminal-btn" onClick={() => applyPreset(p)}>{p.toUpperCase()}</button>
                    ))}
                </div>
            </div>
            <div style={styles.terminalBody}>
                <div style={styles.dateInputGroup}>
                    <Calendar size={14} color="#64748b" />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={styles.dateInputTerminal} />
                    <span style={{color: '#334155', fontWeight: 800}}>—</span>
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={styles.dateInputTerminal} />
                    <Calendar size={14} color="#64748b" />
                </div>
            </div>
        </div>
      </div>

      <div style={styles.statsRow}>
        <div style={styles.heroCard} className="stat-card-glow">
          <div style={styles.heroLabel}><TrendingUp size={18} color="#38bdf8" /> Net PnL</div>
          <div style={{ ...styles.heroValue, color: stats.pnl >= 0 ? "#22c55e" : "#ef4444" }}>
            {stats.pnl >= 0 ? "+" : "-"}{currency}{Math.abs(stats.pnl).toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
        </div>
        <div style={styles.heroCard} className="stat-card-glow">
          <div style={styles.heroLabel}><Award size={18} color="#38bdf8" /> Win Rate</div>
          <div style={styles.heroValue}>{stats.winRate}%</div>
          <div style={styles.miniBar}><div style={{ ...styles.miniProgress, width: `${stats.winRate}%` }}></div></div>
        </div>
        <div style={styles.heroCard} className="stat-card-glow">
          <div style={styles.heroLabel}><Target size={18} color="#38bdf8" /> Profit Factor</div>
          <div style={{ ...styles.heroValue, color: stats.profitFactor > 1.5 ? "#22c55e" : "#fff" }}>{stats.profitFactor.toFixed(2)}</div>
        </div>
      </div>

      <div style={styles.secondaryGrid}>
        <div className="stat-card-small"><span>Total Trades</span><strong>{stats.total}</strong></div>
        <div className="stat-card-small"><span>Avg Win</span><strong style={{ color: "#22c55e" }}>{currency}{stats.avgWin.toFixed(0)}</strong></div>
        <div className="stat-card-small"><span>Avg Loss</span><strong style={{ color: "#ef4444" }}>{currency}{stats.avgLoss.toFixed(0)}</strong></div>
        <div className="stat-card-small"><span>Expectancy</span><strong>{currency}{stats.expectancy.toFixed(2)}</strong></div>
        <div className="stat-card-small"><span>Max Drawdown</span><strong style={{ color: "#f59e0b" }}>{currency}{stats.maxDrawdown.toFixed(0)}</strong></div>
      </div>

      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>
          <span>DATE / DAY</span>
          <span>INSTRUMENT <ColumnFilter column="instrument" trades={trades} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></span>
          <span>SESSION <ColumnFilter column="session" trades={trades} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></span>
          <span>TF <ColumnFilter column="timeframe" trades={trades} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></span>
          <span>RR</span>
          <span>SETUP <ColumnFilter column="setup" trades={trades} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></span>
          <span>MISTAKE <ColumnFilter column="mentalMistake" trades={trades} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></span>
          <span>RATING</span>
          <span>RESULT <ColumnFilter column="result" trades={trades} activeFilters={activeFilters} onFilterChange={handleFilterChange} /></span>
          <span style={{textAlign:'right'}}>PNL</span>
          <span>POST TRADE NOTES</span>
          <span>IMAGE</span>
          <span></span>
        </div>
        <div className="table-body">
          {filteredTrades.map(trade => {
            const isWin = trade.result?.toLowerCase() === "profit";
            const formattedResult = trade.result ? trade.result.charAt(0).toUpperCase() + trade.result.slice(1).toLowerCase() : "—";
            
            return (
              <div key={trade.id} className={`table-row ${isWin ? 'row-win' : 'row-loss'}`}>
                <div style={{display:'flex', flexDirection:'column', alignItems: 'center'}}>
                    <span style={styles.monoMuted}>{trade.date}</span>
                    <span style={styles.dayLabel}>{getDayName(trade.date)}</span>
                </div>
                <span style={{ fontWeight: 600, color: "#f8fafc" }}>{trade.instrument}</span>
                <span>{trade.session}</span>
                <span>{trade.timeframe}</span>
                <span style={styles.mono}>{trade.rr}</span>
                <span style={styles.setupCell}>
                  {trade.reason || trade.setup || "—"}
                </span>
                <span style={{ color: trade.mentalMistake && !trade.mentalMistake.includes("None") ? "#fca5a5" : "#64748b", fontSize: 11 }}>
                    {trade.mentalMistake || "None"}
                </span>
                <div style={{display:'flex', alignItems:'center', justifyContent: 'center', gap: 4}}>
                    <Star size={10} color="#eab308" fill="#eab308" />
                    <span style={{fontSize: 12, fontWeight: 700}}>{trade.tradeRating || trade.rating || "0"}/10</span>
                </div>
                <span className={isWin ? 'badge-win' : 'badge-loss'}>{formattedResult}</span>
                <span style={{ ...styles.monoBold, textAlign: 'right', color: isWin ? "#22c55e" : "#ef4444" }}>
                  {trade.pnl >= 0 ? "+" : "-"}{currency}{Math.abs(trade.pnl).toLocaleString()}
                </span>
                <div style={styles.notesCell} onClick={() => { setEditingTrade(trade); setTempNote(trade.postTradeNotes || ""); }}>
                    <Edit3 size={12} color="#38bdf8" style={{ flexShrink: 0 }} />
                    <span style={styles.wrappedNotesText}>{trade.postTradeNotes || "Add post-trade analysis..."}</span>
                </div>
                <div style={styles.actionCell}>
                  {trade.tradeImageUrl ? (
                    <ImageIcon size={16} color="#38bdf8" style={{cursor: 'pointer'}} onClick={() => setPreviewImage(trade.tradeImageUrl)} />
                  ) : (
                    <span style={{color: '#334155'}}>—</span>
                  )}
                </div>
                <div style={styles.actionCell} onClick={() => deleteTrade(trade.id)}><Trash2 size={16} className="delete-icon" /></div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const customCSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap');
  
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background-color: #020617;
  }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 10px; }
  ::-webkit-scrollbar-thumb:hover { background: #38bdf8; }

  * { box-sizing: border-box; }

  .custom-scrollbar::-webkit-scrollbar { width: 4px; }
  .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
  .custom-scrollbar::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 10px; }
  .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #38bdf8; }
  
  .terminal-btn { background: transparent; border: 1px solid rgba(255,255,255,0.05); color: #64748b; padding: 4px 12px; border-radius: 4px; font-size: 10px; font-weight: 700; cursor: pointer; transition: 0.2s; }
  .terminal-btn:hover { background: #38bdf8; color: #000; border-color: #38bdf8; }
  
  .dropdown-item:hover { background: rgba(56, 189, 248, 0.08) !important; }

  .stat-card-small { background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255,255,255,0.05); padding: 16px; border-radius: 12px; display: flex; flex-direction: column; gap: 4px; transition: 0.3s; }
  .stat-card-small:hover { border-color: rgba(56, 189, 248, 0.2); background: rgba(15, 23, 42, 0.8); }
  .stat-card-small span { color: #64748b; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; }
  .stat-card-small strong { font-size: 16px; color: #f1f5f9; }

  .stat-card-glow { transition: 0.3s; }
  .stat-card-glow:hover { border-color: rgba(56, 189, 248, 0.3) !important; transform: translateY(-2px); }

  .table-row { 
    display: grid; 
    grid-template-columns: 1fr 0.8fr 0.8fr 0.5fr 0.4fr 1fr 1fr 0.7fr 0.8fr 1fr 2.5fr 0.4fr 0.4fr; 
    padding: 12px 24px; 
    border-bottom: 1px solid rgba(255,255,255,0.03); 
    align-items: center; 
    text-align: center;
    width: 100%;
    transition: 0.2s;
  }
  .table-row:hover { background: rgba(255,255,255,0.01); }

  .badge-win { background: rgba(34, 197, 94, 0.1); color: #22c55e; padding: 4px 10px; border-radius: 6px; font-size: 10px; font-weight: 800; margin: 0 auto; }
  .badge-loss { background: rgba(239, 68, 68, 0.1); color: #ef4444; padding: 4px 10px; border-radius: 6px; font-size: 10px; font-weight: 800; margin: 0 auto; }
  .row-win { border-left: 3px solid #22c55e; }
  .row-loss { border-left: 3px solid #ef4444; }
  .delete-icon { color: #475569; transition: color 0.2s; cursor: pointer; }
  .delete-icon:hover { color: #ef4444; }
`;

const styles = {
  container: { width: "100%", margin: "0", color: "#fff", padding: "5px 24px 40px 24px", boxSizing: 'border-box' },
  header: { display: "flex", justifyContent: "space-between", marginBottom: 15, alignItems: "flex-end", flexWrap: 'wrap', gap: 20 },
  mainTitle: { fontSize: 30, fontWeight: 900, color: "#f8fafc", marginBottom: 2, letterSpacing: '-0.02em' },
  subtitle: { color: "#64748b", fontSize: 13, marginBottom: 15 },
  
  dateSelectorTerminal: { background: "rgba(15, 23, 42, 0.6)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 16, padding: "12px", minWidth: "400px" },
  terminalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.03)', paddingBottom: 8 },
  terminalTitle: { fontSize: 9, fontWeight: 800, color: '#475569', letterSpacing: '0.1em' },
  presetGroup: { display: 'flex', gap: 6 },
  terminalBody: { display: 'flex', alignItems: 'center' },
  dateInputGroup: { display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(0,0,0,0.2)', padding: '6px 12px', borderRadius: 8, width: '100%', border: '1px solid rgba(255,255,255,0.05)' },
  dateInputTerminal: { background: 'transparent', border: 'none', color: '#f8fafc', fontSize: 11, outline: 'none', fontFamily: "'JetBrains Mono', monospace" },

  dropdownContainer: { position: 'relative', background: "rgba(15, 23, 42, 0.6)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 16, padding: "12px", width: '380px' },
  dropdownTrigger: { background: 'rgba(0,0,0,0.2)', border: "1px solid rgba(255,255,255,0.05)", padding: "6px 14px", borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", minHeight: '48px' },
  selectedContent: { display: "flex", flexDirection: "column", gap: 1 },
  accName: { fontWeight: 800, fontSize: 13, color: "#f8fafc" },
  accDetails: { fontSize: 10, color: "#64748b" },
  dropdownMenu: { position: "absolute", top: "calc(100% + 8px)", left: 0, right: 0, background: "#0b1221", border: "1px solid rgba(56, 189, 248, 0.2)", borderRadius: "12px", zIndex: 9999, maxHeight: 250, overflowY: "auto" },
  dropdownOption: { padding: "12px 16px", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.03)" },
  optionMain: { fontWeight: 700, fontSize: 13, color: "#f1f5f9" },
  optionSub: { fontSize: 10, color: "#94a3b8", display: 'flex', alignItems: 'center', gap: 10 },
  phaseBadge: { background: "rgba(56, 189, 248, 0.1)", color: "#38bdf8", padding: "1px 6px", borderRadius: "4px", fontSize: 8 },

  statsRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginBottom: 20 },
  heroCard: { background: "rgba(15, 23, 42, 0.6)", borderRadius: 16, padding: 20, border: "1px solid rgba(255,255,255,0.05)" },
  heroLabel: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#94a3b8", fontWeight: 600, marginBottom: 12 },
  heroValue: { fontSize: 28, fontWeight: 900, letterSpacing: '-0.03em', fontFamily: "'JetBrains Mono', monospace" },
  miniBar: { height: 4, background: "rgba(255,255,255,0.05)", borderRadius: 10, marginTop: 12 },
  miniProgress: { height: "100%", background: "#38bdf8", borderRadius: 10 },
  secondaryGrid: { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16, marginBottom: 30 },
  
  tableCard: { background: "rgba(15, 23, 42, 0.6)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 20, width: '100%' },
  tableHeader: { 
    display: "grid", 
    gridTemplateColumns: "1fr 0.8fr 0.8fr 0.5fr 0.4fr 1fr 1fr 0.7fr 0.8fr 1fr 2.5fr 0.4fr 0.4fr", 
    padding: "16px 24px", 
    background: "rgba(255,255,255,0.02)", 
    fontSize: 9, 
    fontWeight: 800, 
    color: "#475569", 
    letterSpacing: "0.1em",
    textAlign: 'center'
  },

  filterMenu: { position: "absolute", top: "20px", left: 0, background: "#0f172a", border: "1px solid #38bdf8", borderRadius: "8px", zIndex: 100, minWidth: "120px", maxHeight: "200px", overflowY: "auto", padding: "4px" },
  filterOption: { padding: "8px 12px", fontSize: "10px", fontWeight: "700", cursor: "pointer", textAlign: "left", borderBottom: "1px solid rgba(255,255,255,0.05)" },

  notesCell: { 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center', 
    gap: 8, 
    padding: '0 10px', 
    cursor: 'pointer', 
    minHeight: '40px',
    textAlign: 'center' 
  },
  wrappedNotesText: { fontSize: 11, color: '#94a3b8', lineHeight: '1.4', whiteSpace: 'normal', wordBreak: 'break-word', display: 'block' },
  setupCell: { color: "#9ca3af", fontSize: 11, textAlign: 'center' },
  actionCell: { display: "flex", justifyContent: "center", alignItems: "center" },
  
  mono: { fontFamily: "'JetBrains Mono', monospace", fontSize: 11 },
  monoBold: { fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 12 },
  monoMuted: { fontFamily: "'JetBrains Mono', monospace", color: "#f8fafc", fontSize: 11 },
  dayLabel: { fontSize: 9, fontWeight: 800, color: "#38bdf8", textTransform: 'uppercase' },

  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10000, padding: '40px' },
  modalContent: { position: 'relative', maxWidth: '90%', maxHeight: '90%' },
  closeBtn: { position: 'absolute', top: '-40px', right: '-40px', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' },
  fullImage: { maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px', border: '1px solid #1e293b' },

  noteEditorBox: { background: '#0f172a', border: '1px solid #38bdf8', borderRadius: '16px', width: '450px', padding: '20px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.5)' },
  noteEditorHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' },
  noteTextarea: { width: '100%', minHeight: '120px', background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#f8fafc', padding: '12px', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit' },
  saveNoteBtn: { marginTop: '15px', width: '100%', background: '#38bdf8', color: '#000', border: 'none', borderRadius: '8px', padding: '10px', fontWeight: '800', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }
};